import { FormControl, InputLabel } from '@material-ui/core';
import { format } from 'date-fns';
import React from 'react';
import { useSelector } from 'react-redux';
import { legacyParse, convertTokens } from '@date-fns/upgrade/v2';

import { currencyFormat } from 'utils/currencyFormat';
import Budget from 'entities/budget/model';
import { BudgetTotals } from 'containers/DonationRulesListPage/utils';
import { Features } from 'utils/features';
import { red } from 'utils/color-styles';
import { selectUser } from 'entities/user/selectors';

import { DonationGivingBudgetAllocationsCorporateDonationSummary } from './DonationGivingBudgetAllocationsCorporateDonationSummary';
import { DonationGivingRulesDonationCreditSummary } from './DonationGivingRulesDonationCreditSummary';
import { DonationGivingRulesDonationMatchingSummary } from './DonationGivingRulesDonationMatchingSummary';
import { BudgetLimitPerUser } from './BudgetLimitPerUser';
import { TotalLimitLabel } from './TotalLimitLabel';
import DonationGivingRulesVolunteerRewardDonationSummary from './DonationGivingRulesVolunteerRewardDonationSummary';

interface Props {
  budget: Budget;
  budgetTotals: BudgetTotals;
  budgetBalanceLow: boolean;
  budgetInactive: boolean;
}

const DonationGivingRulesBudgetSummary = ({
  budget,
  budgetTotals,
  budgetBalanceLow,
  budgetInactive,
}: Props) => {
  const startingAt = budget.get('startingAt');
  const endingAt = budget.get('endingAt');

  const user = useSelector(selectUser);
  const hasMatching = user?.hasFeature(Features.DONATION_MATCHING);
  const hasDonationCredit = user?.hasFeature(Features.DONATION_CREDIT);
  const hasCorporateDonations =
    user?.hasFeature(Features.CORPORATE_DONATIONS) ||
    user?.hasFeature(Features.EXTERNAL_CORPORATE_DONATIONS);
  const hasVolunteerReward = user?.hasFeature(Features.VOLUNTEER_REWARD);

  const showBudgetSummaryBreakdown =
    [hasMatching, hasDonationCredit, hasCorporateDonations].filter(Boolean).length > 1;

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 15,
          opacity: budgetInactive ? 0.5 : 1,
        }}
      >
        <div style={{ width: showBudgetSummaryBreakdown ? '20%' : '15%' }}>
          <FormControl style={{ width: '100%', height: 17 }}>
            <InputLabel shrink>Total Amount</InputLabel>
          </FormControl>
          <div>{currencyFormat(budgetTotals.totalUsed, budget.get('currencyCode'))}</div>
        </div>
        <div style={{ width: showBudgetSummaryBreakdown ? '20%' : '15%' }}>
          <FormControl style={{ width: '100%', height: 17 }}>
            <InputLabel shrink>Total Balance</InputLabel>
          </FormControl>
          <div style={{ color: budgetBalanceLow ? red : undefined }}>
            {Number.isFinite(budgetTotals.balance)
              ? currencyFormat(budgetTotals.balance || 0, budget.get('currencyCode'))
              : '-'}
          </div>
        </div>
        <div style={{ width: showBudgetSummaryBreakdown ? '20%' : '15%' }}>
          <TotalLimitLabel summary />
          <div>
            {budget.get('limitTotal')
              ? currencyFormat(budget.get('limitTotal'), budget.get('currencyCode'))
              : '-'}
          </div>
        </div>
        <div style={{ width: showBudgetSummaryBreakdown ? '20%' : '10%' }}>
          <BudgetLimitPerUser budget={budget} />
        </div>
        {hasMatching && !showBudgetSummaryBreakdown && (
          <div style={{ width: '15%' }}>
            <FormControl style={{ width: '100%', height: 17 }}>
              <InputLabel shrink>Donations Matched</InputLabel>
            </FormControl>
            <div>{budgetTotals.donationsMatchedCount}</div>
          </div>
        )}
        {hasDonationCredit && !showBudgetSummaryBreakdown && (
          <div style={{ width: '15%' }}>
            <FormControl style={{ width: '100%', height: 17 }}>
              <InputLabel shrink>Credits Issued</InputLabel>
            </FormControl>
            <div>{budgetTotals.creditingTransactionsCount}</div>
          </div>
        )}
        {hasCorporateDonations && !showBudgetSummaryBreakdown && (
          <div style={{ width: '15%' }}>
            <FormControl style={{ width: '100%', height: 17 }}>
              <InputLabel shrink>Corporate Donations Made</InputLabel>
            </FormControl>
            <div>{budgetTotals.corporateDonationsCount}</div>
          </div>
        )}
        <div style={{ width: '20%' }}>
          <FormControl style={{ width: '100%', height: 17 }}>
            <InputLabel shrink>Start &amp; End Date</InputLabel>
          </FormControl>
          <div>
            {startingAt || endingAt ? (
              <>
                {startingAt ? format(legacyParse(startingAt), convertTokens('YYYY-MM-DD')) : 'N/A'}
                {' – '}
                {endingAt ? format(legacyParse(endingAt), convertTokens('YYYY-MM-DD')) : 'N/A'}
              </>
            ) : (
              'Not constrained'
            )}
          </div>
        </div>
      </div>
      {showBudgetSummaryBreakdown && (
        <>
          <h5
            style={{
              opacity: budgetInactive ? 0.5 : 1,
            }}
          >
            Matching Rules Summary
          </h5>
          <DonationGivingRulesDonationMatchingSummary
            budget={budget}
            budgetTotals={budgetTotals}
            budgetInactive={budgetInactive}
          />
          <h5
            style={{
              opacity: budgetInactive ? 0.5 : 1,
            }}
          >
            Donation Credit Rules Summary
          </h5>
          <DonationGivingRulesDonationCreditSummary
            budget={budget}
            budgetTotals={budgetTotals}
            budgetInactive={budgetInactive}
          />
          <h5
            style={{
              opacity: budgetInactive ? 0.5 : 1,
            }}
          >
            Corporate Donation Rule Summary
          </h5>
          <DonationGivingBudgetAllocationsCorporateDonationSummary
            budget={budget}
            budgetTotals={budgetTotals}
            budgetInactive={budgetInactive}
          />
          {hasVolunteerReward && (
            <>
              <h5
                style={{
                  opacity: budgetInactive ? 0.5 : 1,
                }}
              >
                Volunteer Grant Rule Summary
              </h5>
              <DonationGivingRulesVolunteerRewardDonationSummary
                budget={budget}
                budgetTotals={budgetTotals}
                budgetInactive={budgetInactive}
              />
            </>
          )}
        </>
      )}
    </>
  );
};

export default DonationGivingRulesBudgetSummary;
